import {
  ILicensePlate,
  IMasterVehicle,
  IMasterVehicleForm,
  IMasterVehicleLicensePlate,
} from '@/models';
import { generateUniqueId } from '@/utils';

type MasterVehicleLike = Pick<
  IMasterVehicle,
  | 'licensePlate'
  | 'licensePlates'
  | 'uniqueId'
  | 'make'
  | 'model'
  | 'year'
  | 'color'
  | 'type'
  | 'owner'
  | '_id'
  | 'isVerified'
  | 'files'
  | 'vin'
  | 'style'
  | 'uniqueCharacteristics'
>;

type MasterVehicleFormValues = Omit<
  Partial<IMasterVehicleForm>,
  'owner' | 'licensePlate' | 'licensePlates'
> & {
  _id?: string;
  uniqueId?: number;
  color?: string;
  make?: string;
  model?: string;
  owner?: IMasterVehicle['owner'];
  licensePlate?: IMasterVehicleLicensePlate;
  licensePlates?: IMasterVehicleLicensePlate[];
};

type LicensePlateCarrier = {
  licensePlate?: ILicensePlate | null;
  licensePlates?: IMasterVehicleLicensePlate[] | null;
};

export const hasLicensePlateValue = (plate?: ILicensePlate | null) => {
  if (!plate) return false;

  return Boolean(plate.number || plate.state || plate.year || plate.type);
};

const normalizePlate = (
  plate?: ILicensePlate | IMasterVehicleLicensePlate | null,
): IMasterVehicleLicensePlate | null => {
  if (!hasLicensePlateValue(plate)) {
    return null;
  }

  return {
    number: plate?.number || '',
    state: plate?.state || '',
    year: plate?.year || '',
    type: plate?.type || '',
    ...(typeof plate === 'object' && plate && 'isActive' in plate
      ? { isActive: Boolean(plate.isActive) }
      : {}),
  };
};

export const getNormalizedLicensePlates = (
  vehicle?: LicensePlateCarrier | null,
): IMasterVehicleLicensePlate[] => {
  const plateList =
    vehicle?.licensePlates
      ?.map((plate) => normalizePlate(plate))
      .filter(Boolean) || [];

  if (plateList.length > 0) {
    const activeIndex = plateList.findIndex((plate) => plate?.isActive);

    return plateList.map((plate, index) => ({
      ...plate!,
      isActive:
        activeIndex >= 0
          ? index === activeIndex
          : plateList.length === 1
          ? true
          : index === 0,
    }));
  }

  const legacyPlate = normalizePlate(vehicle?.licensePlate);
  if (!legacyPlate) {
    return [];
  }

  return [{ ...legacyPlate, isActive: true }];
};

export const getActiveLicensePlate = (
  vehicle?: LicensePlateCarrier | null,
): IMasterVehicleLicensePlate | undefined =>
  getNormalizedLicensePlates(vehicle).find((plate) => plate.isActive);

export const sortActiveLicensePlateFirst = (
  licensePlates: IMasterVehicleLicensePlate[],
) =>
  [...licensePlates].sort(
    (a, b) => Number(Boolean(b.isActive)) - Number(Boolean(a.isActive)),
  );

export const formatLicensePlateLabel = (
  plate?: ILicensePlate | IMasterVehicleLicensePlate | null,
  separator = ' - ',
) => {
  if (!plate) return '';

  const state = plate.state?.trim();
  const number = plate.number?.trim();

  if (state && number) {
    return `${state}${separator}${number}`;
  }

  return number || state || '';
};

export const getLicensePlateSummaryLabel = (
  vehicle?: LicensePlateCarrier | null,
) => formatLicensePlateLabel(getActiveLicensePlate(vehicle), '-');

export const buildMasterVehicleTitle = (
  vehicle?:
    | (LicensePlateCarrier & {
        uniqueId?: string | number;
        make?: IMasterVehicle['make'];
        model?: IMasterVehicle['model'];
      })
    | null,
) => {
  if (!vehicle?.uniqueId) {
    return '';
  }

  const id = generateUniqueId('VEH', vehicle.uniqueId);
  const plate = getActiveLicensePlate(vehicle)?.number || '';
  const make =
    typeof vehicle.make === 'object' && vehicle.make !== null
      ? (vehicle.make as { name?: string })?.name || ''
      : String(vehicle.make || '');
  const model =
    typeof vehicle.model === 'object' && vehicle.model !== null
      ? (vehicle.model as { name?: string })?.name || ''
      : String(vehicle.model || '');

  return [id, plate, make, model].filter(Boolean).join(' - ');
};

export const normalizeMasterVehicleFormValues = (
  vehicle: MasterVehicleLike,
): MasterVehicleFormValues => ({
  ...vehicle,
  _id: vehicle._id,
  uniqueId:
    typeof vehicle.uniqueId === 'string'
      ? Number(vehicle.uniqueId)
      : vehicle.uniqueId,
  year: vehicle.year?.toString(),
  make:
    typeof vehicle.make === 'object' && vehicle.make !== null
      ? (vehicle.make as { name?: string })?.name || ''
      : String(vehicle.make || ''),
  model:
    typeof vehicle.model === 'object' && vehicle.model !== null
      ? (vehicle.model as { name?: string })?.name || ''
      : String(vehicle.model || ''),
  color:
    typeof vehicle.color === 'object' && vehicle.color !== null
      ? (vehicle.color as { _id?: string; name?: string })?._id ||
        (vehicle.color as { _id?: string; name?: string })?.name ||
        ''
      : String(vehicle.color || ''),
  licensePlate: getActiveLicensePlate(vehicle),
  licensePlates: sortActiveLicensePlateFirst(
    getNormalizedLicensePlates(vehicle),
  ).map((plate) => ({
    ...plate,
    year: plate.year?.toString() || '',
  })),
});

export const prepareLicensePlatesForSubmission = (
  licensePlates?: IMasterVehicleLicensePlate[] | null,
) => {
  const normalized = (licensePlates || [])
    .map((plate) => normalizePlate(plate))
    .filter(Boolean) as IMasterVehicleLicensePlate[];

  const withActiveSelection =
    normalized.length === 1
      ? normalized.map((plate) => ({ ...plate, isActive: true }))
      : normalized;

  return {
    licensePlates: withActiveSelection.map((plate) => ({
      ...plate,
      year: plate.year ? parseInt(plate.year.toString(), 10) : undefined,
      isActive:
        withActiveSelection.length === 1 ? true : Boolean(plate.isActive),
      number: plate.number || '',
      state: plate.state || '',
      type: plate.type || '',
    })),
  };
};
